import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ProjectFiltersDialog from '../components/ProjectFiltersDialog';
import MonthRangePicker, { lastNMonthsRange, formatMonthRange, type MonthRange } from '../components/MonthRangePicker';
import DotCarousel from '../components/DotCarousel';
import ProjectNavButtons from '../components/ProjectNavButtons';
import PageSearchBar from '../components/PageSearchBar';
import { PaginatedPosterGrid } from '../components/PaginatedPosterGrid';
import { PosterCard, type PosterItem } from '../components/PosterCard';
import {
  logEdmPreviewed,
  logEdmDownloaded,
  logEbookPreviewed,
  logEbookDownloaded,
} from '../services/analytics';
import { ProjectCard } from './Dashboard';
import { GlsCard } from '../components/GlsCard';
import {
  projectEssentialApi,
  type GlsLandSaleItem,
  type UpcomingProject,
  type EdmTabItem,
  type EdmTabId,
} from '../services/api';
import { useAppliedFilterChips } from '../hooks/useAppliedFilterChips';
import { usePeFilters } from '../hooks/usePeFilters';
import { usePeSearchDropdown } from '../hooks/usePeSearchDropdown';
import {
  buildPeSearchPageHref,
  countPeFilters,
  hasPeProjectFilter,
  peUpcomingGlsHeading,
} from '../utils/searchState';

// ---------------------------------------------------------------------------
// Shared listing page used by the dashboard pre-filter buttons:
//   • /project-essential/upcoming-projects        (variant='upcoming')
//   • /project-essential/commercial-industrial    (variant='commercial')
//
// Same layout for both. Variant only changes the page title, the
// secondary navigation button shown alongside the other category
// buttons, and the labels of the file sections.
// ---------------------------------------------------------------------------

export type ListingVariant = 'upcoming' | 'all-projects' | 'commercial' | 'international';

/** Property-type buckets hidden on All Projects (already SG residential). */
const ALL_PROJECTS_EXCLUDED_PROPERTY_TYPES: string[] = [
  'commercial',
  'local',
  'international',
];

interface VariantCopy {
  /** Hero title at the top of the page. */
  title: string;
}

const VARIANT_COPY: Record<ListingVariant, VariantCopy> = {
  upcoming: {
    title: 'Upcoming GLS / Enbloc',
  },
  'all-projects': {
    title: 'Resi Projects',
  },
  commercial: {
    title: 'Commercial & Industrial',
  },
  international: {
    title: 'International',
  },
};

// ── Placeholder file data (frontend-only) ──────────────────────────────
// Same poster shape (`PosterItem`) the EDM page uses, so the catalogue
// grid renders identically across both surfaces. Replace with a real
// fetch keyed on `variant` once the backend is ready.

type ListingTabId = 'all' | 'highlights' | 'edm';

interface ListingPosterItem extends PosterItem {
  /** Tag used to bucket the file into the right tab. */
  tag: 'highlights' | 'edm';
}

const TABS: Array<{ id: ListingTabId; label: string }> = [
  { id: 'all', label: 'All Files' },
  { id: 'highlights', label: 'Client Engagement eBooks' },
  { id: 'edm', label: 'EDM' },
];

const LISTING_EDM_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming Projects' },
  { id: 'projects', label: 'Projects' },
  { id: 'seminars', label: 'Seminar & Events' },
  { id: 'property-guides', label: 'Property Guides' },
  { id: 'vvip', label: 'VVIP Passes' },
] as const;

type ListingEdmCategoryId = (typeof LISTING_EDM_CATEGORIES)[number]['id'];

function mapEdmRowToPoster(row: {
  id: string;
  title: string;
  createdAt: string | null;
  cover: string | null;
  downloads: number;
  url: string;
  urlPublic: string | null;
  filename: string;
}): ListingPosterItem {
  return {
    id: row.id,
    title: row.title,
    date: row.createdAt
      ? new Date(row.createdAt).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '',
    cover: row.cover,
    downloads: row.downloads,
    url: row.url,
    urlPublic: row.urlPublic,
    filename: row.filename,
    tag: 'edm',
  };
}

function mapEbookEdmRowToPoster(row: EdmTabItem): ListingPosterItem {
  return {
    id: row.id,
    title: row.title,
    date: row.createdAt
      ? new Date(row.createdAt).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '',
    cover: row.cover,
    downloads: row.downloads,
    url: row.url,
    urlPublic: row.urlPublic ?? null,
    filename: row.filename,
    tag: 'highlights',
  };
}

// ── Component ─────────────────────────────────────────────────────────

export default function ProjectsListingPage({ variant }: { variant: ListingVariant }) {
  const navigate = useNavigate();
  const copy = VARIANT_COPY[variant];

  const [tab, setTab] = useState<ListingTabId>('all');
  const [edmCategory, setEdmCategory] = useState<ListingEdmCategoryId>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = usePeFilters();
  const { search, setSearch, dismissDropdownKey, dropdownContent } = usePeSearchDropdown(filters);
  const [monthRange, setMonthRange] = useState<MonthRange>(() => lastNMonthsRange(6));
  // ── Projects carousel ───────────────────────────────────────────────
  // For the `upcoming` variant the carousel is sourced directly from
  // ProjectLandSale rows where ProjectName IS NULL (via /gls-land-sales).
  // All other variants use /upcoming-projects as before.
  const [projects, setProjects] = useState<UpcomingProject[]>([]);
  const [glsItems, setGlsItems] = useState<GlsLandSaleItem[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  // Carousel and file catalogue share the same dimension filters.
  // The server keeps each page's listing scope (Resi / Commercial /
  // International / GLS) and only returns files tagged to projects
  // (or land sales) that match those filters — never the full Project
  // table.
  const carouselFilters = filters;
  const catalogueFilters = filters;
  const hasProjectFilter = hasPeProjectFilter(filters);
  const filtersKey = [
    filters.regions?.join(',') ?? '',
    filters.districts?.join(',') ?? '',
    filters.propertyTypes?.join(',') ?? '',
    filters.projectIds?.join(',') ?? '',
  ].join('|');

  useEffect(() => {
    let mounted = true;
    if (hasProjectFilter && variant === 'upcoming') {
      setGlsItems([]);
      setProjects([]);
      setProjectsLoading(false);
      setProjectsError(null);
      return () => {
        mounted = false;
      };
    }
    setProjectsLoading(true);
    setProjectsError(null);
    if (variant === 'upcoming') {
      projectEssentialApi
        .getGlsLandSales(undefined, carouselFilters)
        .then((res) => {
          if (mounted) setGlsItems(res.items);
        })
        .catch((err) => {
          if (mounted) setProjectsError(err?.message || 'Failed to load upcoming GLS');
        })
        .finally(() => {
          if (mounted) setProjectsLoading(false);
        });
    } else {
      projectEssentialApi
        .getUpcomingProjects('', carouselFilters, variant)
        .then((res) => {
          if (mounted) setProjects(res.items);
        })
        .catch((err) => {
          if (mounted) {
            setProjectsError(err?.message || 'Failed to load upcoming projects');
          }
        })
        .finally(() => {
          if (mounted) setProjectsLoading(false);
        });
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filtersKey tracks filter identity
  }, [filtersKey, variant, hasProjectFilter]);

  const filterCount = countPeFilters(filters);

  const { chips: appliedFilterChips, clearAll: clearAllFilters } = useAppliedFilterChips(
    filters,
    setFilters,
  );

  // ── EDM (catalogue chips + listing scope) ─────────────────────────
  // Uses the same chip ids as ProjectEdmPage (`all` / `upcoming` / …)
  // with `listingVariant` so results stay scoped to this listing page.
  const [livePostersAll, setLivePostersAll] = useState<ListingPosterItem[]>([]);
  const [livePostersChip, setLivePostersChip] = useState<ListingPosterItem[]>([]);
  const [edmChipCounts, setEdmChipCounts] = useState<Partial<Record<ListingEdmCategoryId, number>>>({});

  useEffect(() => {
    let mounted = true;
    projectEssentialApi
      .getEdmsByTab('all', undefined, undefined, catalogueFilters, monthRange, variant)
      .then((res) => {
        if (!mounted) return;
        setLivePostersAll(res.items.map(mapEdmRowToPoster));
      })
      .catch((err) => {
        console.warn('[ProjectsListingPage] listing EDM (all) fetch failed:', err);
        if (mounted) setLivePostersAll([]);
      });
    return () => {
      mounted = false;
    };
  }, [variant, monthRange, filtersKey]);

  useEffect(() => {
    let mounted = true;
    projectEssentialApi
      .getEdmsByTab(edmCategory as EdmTabId, undefined, undefined, catalogueFilters, monthRange, variant)
      .then((res) => {
        if (!mounted) return;
        setLivePostersChip(res.items.map(mapEdmRowToPoster));
      })
      .catch((err) => {
        console.warn(`[ProjectsListingPage] listing EDM (${edmCategory}) fetch failed:`, err);
        if (mounted) setLivePostersChip([]);
      });
    return () => {
      mounted = false;
    };
  }, [edmCategory, variant, monthRange, filtersKey]);

  useEffect(() => {
    let mounted = true;
    projectEssentialApi
      .getEdmTabCounts(undefined, catalogueFilters, monthRange, variant)
      .then((res) => {
        if (!mounted) return;
        setEdmChipCounts(res.counts as Partial<Record<ListingEdmCategoryId, number>>);
      })
      .catch(() => {
        // Count badges are supplementary.
      });
    return () => {
      mounted = false;
    };
  }, [variant, monthRange, filtersKey]);

  // Upcoming GLS listing hides both eBook tabs — skip those fetches.
  const showEbookTabs = variant !== 'upcoming';
  const listingEdmCategories = useMemo(
    () =>
      variant === 'all-projects'
        ? LISTING_EDM_CATEGORIES
        : LISTING_EDM_CATEGORIES.filter((cat) => cat.id !== 'property-guides'),
    [variant],
  );

  const visibleTabs = useMemo(
    () => (showEbookTabs ? TABS : TABS.filter((t) => t.id === 'all' || t.id === 'edm')),
    [showEbookTabs],
  );

  useEffect(() => {
    if (!showEbookTabs && (tab === 'highlights')) {
      setTab('all');
    }
  }, [showEbookTabs, tab]);

  useEffect(() => {
    if (variant !== 'all-projects' && edmCategory === 'property-guides') {
      setEdmCategory('all');
    }
  }, [variant, edmCategory]);

  // ── Client Engagement eBooks (EDM eBooks tab) ────────────────
  // Use `EDM.Thumbnail` so the thumbnail rendering matches eDMs.
  const [liveEbooks, setLiveEbooks] = useState<ListingPosterItem[]>([]);
  useEffect(() => {
    if (!showEbookTabs) {
      setLiveEbooks([]);
      return;
    }
    let mounted = true;
    projectEssentialApi
      .getEdmsByTab('ebooks', undefined, undefined, catalogueFilters, monthRange, variant)
      .then((res) => {
        if (!mounted) return;
        const mapped: ListingPosterItem[] = res.items.map((row, i) => ({
          ...mapEbookEdmRowToPoster(row),
        }));
        setLiveEbooks(mapped);
      })
      .catch((err) => {
        // Non-fatal: empty Highlights tab on failure rather than
        // showing stale mock data.
        console.warn(`[ProjectsListingPage] edms/tab(ebooks, ${variant}) fetch failed:`, err);
      });
    return () => {
      mounted = false;
    };
  }, [showEbookTabs, variant, monthRange, filtersKey]);

  // Compose the dataset: EDMs and client-engagement eBooks.
  const allPosters = useMemo<ListingPosterItem[]>(
    () => [...livePostersAll, ...liveEbooks],
    [livePostersAll, liveEbooks],
  );

  const edmChipPosters = livePostersChip;

  const tabCounts = useMemo<Record<ListingTabId, number>>(() => ({
    all: allPosters.length,
    highlights: allPosters.filter((p) => p.tag === 'highlights').length,
    edm: allPosters.filter((p) => p.tag === 'edm').length,
  }), [allPosters]);

  const filteredPosters = useMemo<ListingPosterItem[]>(() => {
    if (tab === 'edm') return edmChipPosters;
    if (tab === 'all') return allPosters;
    return allPosters.filter((p) => p.tag === tab);
  }, [tab, allPosters, edmChipPosters]);

  const handlePreview = (item: PosterItem) => {
    const isEbook = (item as ListingPosterItem).tag !== 'edm';
    if (isEbook) {
      logEbookPreviewed(item.id);
    } else {
      logEdmPreviewed(item.id);
    }
  };

  const handleDownload = (item: PosterItem) => {
    const isEbook = (item as ListingPosterItem).tag !== 'edm';
    if (isEbook) {
      logEbookDownloaded(item.id);
    } else {
      logEdmDownloaded(item.id);
    }
  };

  const filteredCarouselProjects = useMemo(() => projects, [projects]);

  return (
    <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ minWidth: 0 }}>
      {/* Back to Dashboard — pill button matching the Highlights /
          EDM / Seminars / Calendar pages so the back affordance is
          identical across the whole app. */}
      <Box>
        <Button
          onClick={() => navigate('/project-essential')}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            borderColor: 'var(--color-border-strong)',
            color: 'var(--color-text-strong)',
            bgcolor: 'var(--color-surface)',
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
            },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: 'var(--color-accent)',
          textAlign: 'center',
          letterSpacing: '0.01em',
          fontSize: { xs: '1.6rem', sm: '2rem', md: '2.25rem' },
        }}
      >
        {variant === 'upcoming' ? peUpcomingGlsHeading(filters) : copy.title}
      </Typography>

      {/* Shared search + filter row. `loading` reflects the project
          carousel's in-flight state — same UX as the Dashboard. */}
      <PageSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search projects, EDMs, eBooks and reports…"
        onSubmit={(query) => navigate(buildPeSearchPageHref(query, filters))}
        filterCount={filterCount}
        onOpenFilters={() => setFiltersOpen(true)}
        loading={projectsLoading}
        appliedFilters={appliedFilterChips}
        onClearAllFilters={clearAllFilters}
        dropdownContent={dropdownContent}
        dismissDropdownKey={dismissDropdownKey}
      />

      {/* Pre Filters + Category buttons — shared with all other
          category pages so the navigation grammar stays consistent. */}
      <ProjectNavButtons active={variant} />

      {/* Projects carousel — same `ProjectCard` rail as the Dashboard.
          Rendered on both variants; commercial / international stay
          scoped by listing `variant` on the server. User-selected
          property types (e.g. Mixed carried from Resi) still apply.
          Sits above the file tabs so users see live projects first,
          then drill into the per-tag file kits below. */}
      {/* Projects / GLS carousel — on the GLS page, hide only the
          carousel when a specific project is filtered (GLS rows are
          not projects); the section heading stays visible. */}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: 'var(--color-text-strong)',
            letterSpacing: '0.01em',
            fontSize: { xs: '1.15rem', sm: '1.25rem' },
            mb: 1.5,
            textAlign: 'left',
          }}
        >
          {(variant === 'upcoming' ? peUpcomingGlsHeading(filters) : 'Projects').toUpperCase()}{' '}
          {!(hasProjectFilter && variant === 'upcoming') &&
          (variant === 'upcoming' ? glsItems : filteredCarouselProjects).length > 0
            ? `(${(variant === 'upcoming' ? glsItems : filteredCarouselProjects).length})`
            : ''}
        </Typography>
        {hasProjectFilter && variant === 'upcoming' ? (
          <Typography
            variant="body2"
            sx={{ color: 'var(--color-text-muted)', py: 1 }}
          >
            No upcoming GLS right now.
          </Typography>
        ) : projectsLoading && (variant === 'upcoming' ? glsItems : filteredCarouselProjects).length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : projectsError ? (
          <Typography variant="body2" color="error">
            {projectsError}
          </Typography>
        ) : (variant === 'upcoming' ? glsItems : filteredCarouselProjects).length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: 'var(--color-text-muted)', py: 1 }}
          >
            {variant === 'commercial'
              ? 'No commercial or industrial projects right now.'
              : variant === 'international'
              ? 'No international projects right now.'
              : variant === 'all-projects'
              ? 'No projects right now.'
              : 'No upcoming GLS right now.'}
          </Typography>
        ) : (
          <Box
            sx={{
              position: 'relative',
              transition: 'opacity 150ms ease',
              opacity: projectsLoading ? 0.55 : 1,
            }}
          >
            {variant === 'upcoming' ? (
              <DotCarousel
                slides={glsItems.map((item) => ({
                  key: String(item.id),
                  node: <GlsCard item={item} />,
                }))}
                slideFlex={{
                  xs: '0 0 50%',
                  sm: '0 0 40%',
                  md: '0 0 32%',
                  lg: '0 0 32%',
                }}
                gap={{ xs: 0.5, sm: 1.25 }}
              />
            ) : (
              <DotCarousel
                slides={filteredCarouselProjects.map((p) => ({
                  key: p.id,
                  node: <ProjectCard project={p} />,
                }))}
                slideFlex={{
                  xs: '0 0 50%',
                  sm: '0 0 40%',
                  md: '0 0 32%',
                  lg: '0 0 32%',
                }}
                gap={{ xs: 0.5, sm: 1.25 }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Files Section Header Row with Month Range Picker */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          justifyContent: 'space-between',
          gap: 1.5,
          mt: 2,
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: 'var(--color-text-strong)',
              letterSpacing: '0.01em',
              fontSize: { xs: '1.15rem', sm: '1.25rem' },
              lineHeight: 1.2,
            }}
          >
            PROJECT FILES
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'error.main',
              fontSize: '0.9rem',
              fontWeight: '900 !important',
            }}
          >
            Showing files from {formatMonthRange(monthRange)}
          </Typography>
        </Stack>
        <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
          <MonthRangePicker
            value={monthRange}
            onChange={setMonthRange}
          />
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, next: ListingTabId) => setTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 40,
          borderBottom: '1px solid var(--color-border-strong)',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 700,
            color: 'var(--color-text-muted)',
            minHeight: 40,
            px: { xs: 1.25, sm: 2 },
          },
          '& .Mui-selected': { color: 'var(--color-accent) !important' },
          '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)' },
        }}
      >
        {visibleTabs.map((t) => (
          <Tab
            key={t.id}
            value={t.id}
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <span>{t.label}</span>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 22,
                    height: 20,
                    px: 0.75,
                    borderRadius: 999,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    bgcolor:
                      tab === t.id ? 'var(--color-accent)' : 'var(--color-surface-elevated)',
                    color:
                      tab === t.id ? '#fff' : 'var(--color-text-muted)',
                    border:
                      tab === t.id
                        ? '1px solid var(--color-accent)'
                        : '1px solid var(--color-border-strong)',
                    transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
                  }}
                >
                  {tabCounts[t.id]}
                </Box>
              </Box>
            }
          />
        ))}
      </Tabs>

      {tab === 'edm' && (
        <Tabs
          value={edmCategory}
          onChange={(_e, next: ListingEdmCategoryId) => setEdmCategory(next)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            borderBottom: '1px solid var(--color-border-strong)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              color: 'var(--color-text-muted)',
              minHeight: 40,
              px: { xs: 1.25, sm: 2 },
            },
            '& .Mui-selected': { color: 'var(--color-accent) !important' },
            '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)' },
          }}
        >
          {listingEdmCategories.map((cat) => {
            const count = edmChipCounts[cat.id];
            const isActive = edmCategory === cat.id;
            return (
              <Tab
                key={cat.id}
                value={cat.id}
                label={
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    <span>{cat.label}</span>
                    {count != null && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 22,
                          height: 20,
                          px: 0.75,
                          borderRadius: 999,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          bgcolor: isActive ? 'var(--color-accent)' : 'var(--color-surface-elevated)',
                          color: isActive ? '#fff' : 'var(--color-text-muted)',
                          border: isActive
                            ? '1px solid var(--color-accent)'
                            : '1px solid var(--color-border-strong)',
                        }}
                      >
                        {count}
                      </Box>
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      )}

      {/* Poster grid — same paginated, swipe-enabled card grid that
          the Project EDM page uses. The `resetKey` snaps back to page 1
          whenever the user changes tab, search, or monthRange so they're never
          stranded on a now-empty page. */}
      <PaginatedPosterGrid
        items={filteredPosters}
        emptyLabel={`No files match your filter for ${formatMonthRange(monthRange)}.`}
        resetKey={`${tab}|${edmCategory}|${monthRange.start}~${monthRange.end}`}
        onPreview={handlePreview}
        onDownloadClick={handleDownload}
        renderItem={(item) => {
          const listing = item as ListingPosterItem;
          return (
            <PosterCard
              item={listing}
              onPreview={handlePreview}
              onDownloadClick={handleDownload}
              shareKind={listing.tag === 'edm' ? 'edm' : 'ebook'}
            />
          );
        }}
      />

      <ProjectFiltersDialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={(next) => {
          navigate(buildPeSearchPageHref(search.trim(), next));
        }}
        // Resi Projects and GLS are already SG residential — drop
        // commercial / local / international buckets. GLS property type
        // is LandUse-backed (Condo / EC / Mixed / Landed). Commercial
        // and International still show the full Property Type list so
        // a Mixed (etc.) selection carried from Resi stays visible.
        excludePropertyTypes={
          variant === 'all-projects' || variant === 'upcoming'
            ? ALL_PROJECTS_EXCLUDED_PROPERTY_TYPES
            : undefined
        }
      />
    </Stack>
  );
}

